import { logger } from '@gem/modules/logger';

import { initUntrustedProject } from 'APP/Tasks/authorization/login/initUntrustedProject';
import { refreshToken as apiRefreshToken } from 'APP/model/auth/authModel';
import { ILoginSuccess, LoginErrors } from 'APP/model/auth/authModel.types';
import { DeviceStatus } from 'APP/model/userSettings/userSettingsModel.types';
import { storage } from 'APP/packages/storage';
import { RouterLink } from 'APP/router/constants';
import { navigateTo } from 'APP/router/refs/navigateTo';
import { MlsStorageService } from 'SERVICES/mlsStorageService/MlsStorageService';
import { WebCryptoService } from 'SERVICES/webCryptoService/WebCryptoService';
import Entities from 'STORE';

import { getDeviceInfoForLogin } from './getDeviceInfoForLogin';
import { initProject } from './initProject';
import { IRefreshTokenResult, RefreshTokenInitiator } from './login.types';

let refreshTokenPromise: Promise<IRefreshTokenResult> | null = null;
let refreshTokenInitiator: RefreshTokenInitiator | null = null;

export async function refreshTokenFunc(initStore = true): Promise<IRefreshTokenResult> {
  const device = getDeviceInfoForLogin();
  const refreshToken = storage.refreshToken.get();

  if (!refreshToken) {
    return { isSuccess: false, token: null };
  }

  try {
    const response: ILoginSuccess = await apiRefreshToken({
      refreshToken,
      device,
    });

    if (!response.accessToken || !response.user || !response.refreshToken) {
      return { isSuccess: false, token: null, error: new Error('401 Unauthorized') };
    }

    let skipE2ee = false;
    if (response.deviceStatus === DeviceStatus.Pending) {
      Entities.authStore.setIsUntrusted(true);
      navigateTo({ to: RouterLink.Untrusted });
      MlsStorageService.clearStorages();
      WebCryptoService.clearStorages();
      const initResult = await initUntrustedProject({
        userId: response.user.id,
        accessToken: response.accessToken,
        deviceKeyPackagesCount: response.deviceKeyPackagesCount,
        refreshToken: response.refreshToken,
      });

      skipE2ee = true;
      if (!initResult.result) {
        throw new Error(LoginErrors.DeviceIsBlocked);
      }
    }

    //TODO: change to await initProject(response) when will move to real b2b server
    await initProject(
      {
        refreshToken: response.refreshToken,
        accessToken: response.accessToken,
        user: response.user,
        clientConfig: response.clientConfig,
        deviceKeyPackagesCount: response.deviceKeyPackagesCount,
        deviceStatus: response.deviceStatus,
      },
      initStore,
      undefined,
      skipE2ee
    );

    return { isSuccess: true, token: response.accessToken };
  } catch (error) {
    if (error.message === LoginErrors.DeviceIsBlocked) {
      Entities.authStore.setIsUntrusted(true);
      navigateTo({ to: RouterLink.Blocked });

      return { isSuccess: false, token: null, error: new Error('Device is Blocked') };
    }

    logger.get('auth').error('refreshToken', error);
    return { isSuccess: false, token: null, error };
  }
}

export async function refreshToken(
  initStore = true,
  initiator?: RefreshTokenInitiator
): Promise<IRefreshTokenResult> {
  const prevRefreshInitiator = refreshTokenInitiator;
  refreshTokenInitiator = initiator || null;

  if (refreshTokenPromise) {
    const result = await refreshTokenPromise;
    refreshTokenPromise = null;
    return result;
  }

  if (initiator && prevRefreshInitiator && prevRefreshInitiator !== initiator) {
    return { isSuccess: true, token: Entities.authStore.session };
  }

  refreshTokenPromise = refreshTokenFunc(initStore);

  const result = await refreshTokenPromise;
  refreshTokenPromise = null;

  return result;
}
